/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Global listeners gated by REACT_APP_LEGACY_UPLOAD_COMPAT (default OFF).
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * useBasketAutoReopen — parent-side recovery for the iOS Photos
 * picker handoff.
 *
 * Wired into every surface that mounts `ClipBasketModal`. On mount,
 * pageshow, visibilitychange and focus events, the hook reads the
 * picker beacon (sessionStorage). If a fresh beacon is present AND
 * the modal is currently closed, the hook re-opens the modal with
 * `resumeBasketId = beacon.basket_id` and surfaces a calm
 * "Welcome back from Photos" toast. The modal itself clears the
 * beacon once it has successfully resumed the basket — so the
 * loop self-terminates.
 *
 * Operator brief (May 20, 2026):
 *   "After selecting videos from iPhone Photos, every role lands in
 *    a visible persistent basket/import state. user is never dumped
 *    back to the start screen."
 *
 * Doctrine guard: tests/test_upload_picker_continuity.py asserts
 * every ClipBasketModal entry point uses this hook.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { readPickerBeacon, clearPickerBeacon } from "../lib/pickerBeacon";
import { emitPickerEvent } from "../lib/pickerDebug";
import { legacyUploadCompatEnabled } from "../lib/legacyUploadCompat";

export default function useBasketAutoReopen({
  basketOpen,
  setBasketOpen,
  setResumeBasketId,
  source,
  enabled = true,
}) {
  // Toast guard — we only want to fire the recovery toast ONCE
  // per beacon, even if pageshow + visibilitychange + focus all
  // fire in quick succession.
  const reopenedForRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    // LEGACY_DECOMMISSIONED — quarantine gate. Default OFF.
    // Auto-reopen runs only when REACT_APP_LEGACY_UPLOAD_COMPAT=true.
    if (!legacyUploadCompatEnabled()) return undefined;
    if (typeof window === "undefined") return undefined;

    const tryRecover = (trigger) => {
      // Already open? Nothing to recover — the modal survived the
      // handoff and is doing its own beacon-clear on mount.
      if (basketOpen) return;
      const beacon = readPickerBeacon();
      if (!beacon) return;

      // Defence in depth: only THIS surface should react to a
      // beacon tagged with its own `source`. Prevents cross-role
      // leakage (e.g. an ADI training-upload beacon reopening the
      // learner self-review modal).
      if (source && beacon.source && beacon.source !== source) {
        // eslint-disable-next-line no-console
        console.debug("[picker-beacon] source_mismatch_ignored", {
          beacon_source: beacon.source,
          surface_source: source,
          trigger,
        });
        return;
      }

      if (reopenedForRef.current === beacon.basket_id) {
        // Already reopened for this exact basket — don't double-fire.
        return;
      }
      reopenedForRef.current = beacon.basket_id;

      // eslint-disable-next-line no-console
      console.warn("[picker-beacon] auto_reopen", {
        basket_id: beacon.basket_id,
        source: beacon.source,
        age_ms: beacon.age_ms,
        trigger,
      });

      emitPickerEvent("handoff_recovered", {
        source: beacon.source,
        basket_id: beacon.basket_id,
        age_ms: beacon.age_ms,
        trigger,
      });

      setResumeBasketId?.(beacon.basket_id);
      setBasketOpen?.(true);
      toast.message("Welcome back from Photos — your basket is here.", {
        duration: 4000,
      });
    };

    // Run once on mount.
    tryRecover("mount");

    const onPageShow = () => tryRecover("pageshow");
    const onFocus = () => tryRecover("focus");
    const onVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        tryRecover("visibilitychange");
      }
    };

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, basketOpen, setBasketOpen, setResumeBasketId, source]);

  // Allow the parent to opportunistically clear the beacon (e.g.
  // when the user explicitly closes the basket — recovery is no
  // longer wanted).
  return {
    clearBeacon: () => {
      reopenedForRef.current = null;
      clearPickerBeacon("parent_clear");
    },
  };
}
